import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HOC from '../../components/HOC/HOC';
import { SuccessfullyModal } from '../../components/Modals/Modals';
import addjob from '../../css/style.module.css';
import { getApi, putApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import TimesheetCompleteModal from './TimesheetCompleteModal';
import {
  completeStaffJob,
  getAdminCompleteWindow,
  getApprovalColor,
  getApprovalLabel,
} from './timesheetJobActions';
import {
  formatTimesheetDateTimeDisplay,
} from './timesheetTimeUtils';

const EditTimesheet = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [timesheet, setTimesheet] = useState(null);
  const [partnerId, setPartnerId] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerList, setPartnerList] = useState([]);
  const [toTime, setToTime] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [status, setStatus] = useState('');

  const fetchData = useCallback(async () => {
    await getApi(endPoints.getsTimesheetbyid(id), {
      setResponse: response => {
        const data = response?.data || null;
        setTimesheet(data);
        setPartnerId(data?.staff?._id || '');
        setPartnerName(data?.staff?.firstName || '');
        setToTime(data?.fromTime || '');
        setFromTime(data?.toTime || '');
        setStatus(data?.jobStatus || '');
      },
      setLoading,
      errorMsg: 'Failed to fetch data!',
    });
  }, [id]);

  const handleUpdate = async () => {
    const payload = {
      staffId: partnerId,
    };

    if (status && status !== 'Completed') {
      payload.jobStatus = status;
    }

    await putApi(endPoints.updatedTimesheetById(id), payload, {
      setLoading,
      successMsg: 'Timesheet updated successfully!',
      errorMsg: 'Failed to update timesheet!',
    });
    setShow(true);

    setTimeout(() => {
      setShow(false);
      navigate('/timesheet-Management');
    }, 2000);
  };

  const handleCompleteConfirm = async () => {
    setCompleteLoading(true);
    const ok = await completeStaffJob(id, { onSuccess: fetchData });
    setCompleteLoading(false);

    if (ok) {
      setShowCompleteModal(false);
      setShow(true);
      setTimeout(() => {
        setShow(false);
        navigate('/timesheet-Management');
      }, 2000);
    }
  };

  const fetchPartner = async () => {
    await getApi(endPoints.getallstaff, {
      setResponse: setPartnerList,
      setLoading: setLoading,
      errorMsg: 'Failed to fetch partners!',
    });
  };

  useEffect(() => {
    fetchPartner();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const staffName = timesheet?.staff
    ? `${timesheet.staff.firstName || ''} ${timesheet.staff.lastName || ''}`.trim()
    : '';
  const customerName = timesheet?.user
    ? `${timesheet.user.firstName || ''} ${timesheet.user.lastName || ''}`.trim()
    : '';
  const completeWindow = getAdminCompleteWindow(timesheet?.toTime);
  const showCompleteJobButton = timesheet?.jobStatus !== 'Completed';

  return (
    <>
      <TimesheetCompleteModal
        show={showCompleteModal}
        onHide={() => !completeLoading && setShowCompleteModal(false)}
        onConfirm={handleCompleteConfirm}
        completing={completeLoading}
        staffName={staffName}
        customerName={customerName}
        scheduledEnd={formatTimesheetDateTimeDisplay(timesheet?.toTime)}
        completeUntil={formatTimesheetDateTimeDisplay(completeWindow.closesAt)}
      />
      <SuccessfullyModal
        show={show}
        handleClose={() => setShow(false)}
        text={'Successfully Updated'}
      />
      <div className={addjob.jobmanagementCotainer}>
        <div className={addjob.jobtoppart}>
          <div className={addjob.pagetitle}>
            <h6>Edit Timesheet</h6>
          </div>
          <div className={addjob.rightbuttons}>
            <div className={addjob.rightbuttons2} onClick={() => navigate(-1)}>
              <p>View Timesheets</p>
            </div>
          </div>
        </div>

        <div className={addjob.addjobcontainer}>
          <div className={addjob.addjobsubcontainer}>
            <div className={addjob.addjobtopic}>
              <h6>Timesheet Details</h6>
            </div>
            <div className={addjob.addjobinputs}>
              <label htmlFor="">Select Employee*</label>
              <select
                value={partnerId}
                onChange={e => {
                  const selectedPartner = partnerList?.data?.find(
                    cat => cat._id === e.target.value
                  );
                  setPartnerId(e.target.value);
                  setPartnerName(selectedPartner?.name || '');
                }}
              >
                <option value="">Select Partner</option>
                {partnerList?.data?.map(partner => (
                  <option key={partner._id} value={partner._id}>
                    {partner.firstName} {partner.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className={addjob.addnewbanner}>
              <div className={addjob.addjobtopic}>
                <h6>Timings</h6>
              </div>
              <div className={addjob.addvouchercontainer}>
                <div className={addjob.addjobinputs}>
                  <label htmlFor="">Start Date *</label>
                  <input
                    type="text"
                    value={formatTimesheetDateTimeDisplay(toTime)}
                    readOnly
                  />
                </div>
                <div className={addjob.addjobinputs}>
                  <label htmlFor="">End Date*</label>
                  <input
                    type="text"
                    value={formatTimesheetDateTimeDisplay(fromTime)}
                    readOnly
                  />
                </div>
                <div className={addjob.addjobinputs}>
                  <label htmlFor="">Status*</label>
                  {status === 'Completed' ? (
                    <input type="text" value="Completed" readOnly />
                  ) : (
                    <select onChange={e => setStatus(e.target.value)} value={status}>
                      <option value="">Select</option>
                      <option value="Pending">Pending</option>
                      <option value="Started">Started</option>
                      <option value="Upcoming">Upcoming</option>
                    </select>
                  )}
                </div>
                <div className={addjob.addjobinputs}>
                  <label htmlFor="">Customer Approval</label>
                  <input
                    type="text"
                    value={getApprovalLabel(timesheet)}
                    readOnly
                    style={{
                      color: getApprovalColor(timesheet),
                      fontWeight: 600,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={addjob.addjobbutton}>
            <div className={addjob.rightbuttons}>
              <button onClick={() => navigate(-1)}>Cancel</button>
            </div>
            {showCompleteJobButton && (
              <div className={addjob.rightbuttons}>
                <button
                  type="button"
                  onClick={() => setShowCompleteModal(true)}
                  disabled={completeLoading || loading}
                  style={{ background: '#00AD1D' }}
                >
                  {completeLoading ? 'Completing...' : 'Complete Job'}
                </button>
              </div>
            )}
            <div className={addjob.rightbuttons}>
              <button onClick={handleUpdate} style={{ background: '#F05423' }} disabled={loading}>
                {loading ? 'Updating...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HOC(EditTimesheet);
